/**
 * @author AgentStore
 * @description 路由守卫，目前两种模式：all模式与intelligence模式
 */
import router from '@/router'
import store from '@/store'
import {ElMessage} from 'element-plus'
import i18n, {getLocale, initLocale} from '@/locales/i18n'
import getPageTitle from '@/utils/pageTitle'
import {routesWhiteList} from '@/settings'
import {isAuthenticated, openLoginDialog} from '@/utils/authGate'

function hasPermission(roles, permissionRoles) {
  if (roles.indexOf('is_super') >= 0) return true // admin permission passed directly
  if (!permissionRoles) return true
  return roles.some((role) => permissionRoles.indexOf(role) >= 0)
}

function isRouteWhiteListed(to) {
  return routesWhiteList.indexOf(to.path) !== -1 ||
    (to.matched && to.matched.some(route => routesWhiteList.indexOf(route.path) !== -1))
}

function isPublicRoute(to) {
  return (to.matched && to.matched.some(route => route.meta && route.meta.public === true)) ||
    isRouteWhiteListed(to)
}

function getQueryString(value) {
  return typeof value === 'string' ? value : ''
}

function handleLoginBridge(to, next) {
  if (to.query.login !== '1') return false

  if (!isAuthenticated()) {
    openLoginDialog({
      reason: '',
      redirect: getQueryString(to.query.redirect),
      inviteCode: getQueryString(to.query.invite_code)
    })
  }

  const query = {...to.query}
  delete query.login
  delete query.redirect
  delete query.invite_code
  next({path: to.path, query, hash: to.hash, replace: true})
  return true
}

router.beforeEach(async(to, from, next) => {
  await initLocale(getLocale())
  if (handleLoginBridge(to, next)) return

  const hasToken = isAuthenticated()
  const publicRoute = isPublicRoute(to)
  if (hasToken) {
    if (to.path === '/login') {
      next({path: '/'})
    } else {
      const roles = store.getters['user/roles']
      const hasRoles = roles && roles.length > 0
      if (hasRoles) {
        // 增加权限判断
        if (hasPermission(roles, to.meta.roles) && to.name) {
          next()
        } else {
          next({
            path: '/404',
            replace: true,
            query: {
              noGoBack: true
            }
          })
        }
      } else {
        try {
          const {has_roles} = await store.dispatch('user/getUserInfo')
          // generate accessible routes map based on roles
          const accessRoutes = await store.dispatch('permission/generateRoutes', has_roles)
          // dynamically add accessible routes
          accessRoutes.forEach((item) => {
            router.addRoute(item)
          })
          // hack method to ensure that addRoutes is complete
          // set the replace: true, so the navigation will not leave a history record
          next({...to, replace: true})
        } catch (error) {
          const errorCode = Number(
            error?.response?.data?.code ?? error?.response?.status ?? error?.code
          )
          if (error && error.message && errorCode !== 401) {
            let message = error.message
            if (message === 'Network Error') {
              message = '后端接口连接异常'
            }
            ElMessage.error(message)
          }
          await store.dispatch('user/resetAll')
          if (publicRoute) {
            next()
          } else {
            openLoginDialog({reason: 'common.account_logout_need_relogin', redirect: to.fullPath})
            next({path: '/home', replace: true})
          }
        }
      }
    }
  } else {
    if (publicRoute) {
      next()
    } else {
      openLoginDialog({reason: 'auth_required', redirect: to.fullPath})
      next({path: '/home', replace: true})
    }
  }
})
router.afterEach(async(to) => {
  const title = to.meta.title || ''
  document.title = getPageTitle(i18n.global.t(title))
})
